<script setup lang="ts">
  import { computed, inject, onBeforeUnmount, onMounted, useAttrs } from 'vue';
  import FontIcon from '../../FontIcon/FontIcon.vue';
  import { Tooltip, TooltipPosition } from '../../Tooltip';
  import { DROPDOWN_CONTEXT_KEY } from '../Dropdown.context';
  import {
    dropdownItemClass,
    dropdownItemDisabledClass,
    dropdownItemIconClass,
    dropdownItemSelectedClass,
    dropdownItemTooltipTriggerClass,
  } from './DropdownItem.css';
  import type {
    DropdownItemEmits,
    DropdownItemProps,
  } from './DropdownItem.types';

  defineOptions({ inheritAttrs: false });

  const props = defineProps<DropdownItemProps>();

  const emit = defineEmits<DropdownItemEmits>();

  const attrs = useAttrs();

  const dropdown = inject(DROPDOWN_CONTEXT_KEY, null);

  const isSelected = computed(
    () =>
      props.value !== undefined &&
      dropdown?.selectedValue.value === props.value,
  );

  const isOption = computed(
    () => props.value !== undefined && !!dropdown?.isListbox.value,
  );

  const showDisabledReason = computed(
    () => !!props.disabled && !!props.disabledReason,
  );

  const itemClass = computed(() => [
    dropdownItemClass,
    {
      [dropdownItemSelectedClass]: isSelected.value,
      [dropdownItemDisabledClass]: props.disabled,
    },
  ]);

  const buttonAttrs = computed(() => ({
    'data-test-id': props.testId,
    id: props.id,
    title: props.title,
    role: isOption.value ? 'option' : undefined,
    'aria-selected': isOption.value ? isSelected.value : undefined,
    'aria-disabled': props.disabled || undefined,
    tabindex: isOption.value
      ? dropdown?.tabStopValue.value === props.value
        ? 0
        : -1
      : undefined,
    ...attrs,
    class: [itemClass.value, attrs.class],
  }));

  const onSelect = (event: MouseEvent) => {
    if (props.disabled) {
      event.preventDefault();
      return;
    }
    event.stopPropagation();
    if (props.value !== undefined) {
      dropdown?.selectItem(props.value);
    }
    emit('click', event);
    dropdown?.close();
  };

  onMounted(() => {
    if (props.value !== undefined) {
      dropdown?.registerItem(props.value, props.label);
    }
  });

  onBeforeUnmount(() => {
    if (props.value !== undefined) {
      dropdown?.unregisterItem(props.value);
    }
  });
</script>

<template>
  <Tooltip
    v-if="showDisabledReason"
    :position="TooltipPosition.TOP"
    :description="disabledReason"
  >
    <span :class="dropdownItemTooltipTriggerClass">
      <button type="button" v-bind="buttonAttrs" @click="onSelect">
        <FontIcon
          v-if="icon"
          :icon-name="icon"
          :class="dropdownItemIconClass"
        />
        <span>{{ label }}</span>
      </button>
    </span>
  </Tooltip>
  <button v-else type="button" v-bind="buttonAttrs" @click="onSelect">
    <FontIcon v-if="icon" :icon-name="icon" :class="dropdownItemIconClass" />
    <span>{{ label }}</span>
  </button>
</template>
